import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import UserService, { CreateUserData, UpdateUserData } from '../services/UserService';
import AuthService, { User } from '../services/AuthService';

interface UserAdminProps {
  onBack: () => void;
}

const UserAdmin: React.FC<UserAdminProps> = ({ onBack }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<CreateUserData>({
    username: '',
    password: '',
    email: '',
    role: 'user'
  });
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  const currentUser = AuthService.getUser();

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const fetchedUsers = await UserService.getAllUsers();
      setUsers(fetchedUsers);
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Fehler beim Laden der Benutzer';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = () => {
    setIsEditing(false);
    setSelectedUser(null);
    setFormData({
      username: '',
      password: '',
      email: '',
      role: 'user'
    });
    setDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setIsEditing(true);
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: '',
      email: user.email,
      role: user.role
    });
    setDialogOpen(true);
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setConfirmDialogOpen(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      await UserService.deleteUser(userToDelete.id);
      setUsers(users.filter(u => u.id !== userToDelete.id));
      showSnackbar('Benutzer erfolgreich gelöscht', 'success');
    } catch (err) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Fehler beim Löschen des Benutzers';
      showSnackbar(errorMessage, 'error');
    } finally {
      setConfirmDialogOpen(false);
      setUserToDelete(null);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.username || (!isEditing && !formData.password) || !formData.email) {
      showSnackbar('Bitte füllen Sie alle erforderlichen Felder aus', 'error');
      return;
    }
    
    try {
      if (isEditing && selectedUser) {
        // Update existing user
        const updateData: UpdateUserData = {
          email: formData.email,
          role: formData.role
        };
        
        // Only include password if it was provided
        if (formData.password) {
          updateData.password = formData.password;
        }
        
        const updatedUser = await UserService.updateUser(selectedUser.id, updateData);
        setUsers(users.map(u => u.id === selectedUser.id ? updatedUser : u));
        showSnackbar('Benutzer erfolgreich aktualisiert', 'success');
      } else {
        // Create new user
        const newUser = await UserService.createUser(formData);
        setUsers([...users, newUser]);
        showSnackbar('Benutzer erfolgreich erstellt', 'success');
      }
      
      setDialogOpen(false);
    } catch (err) {
      const errorMessage = err instanceof Error 
        ? err.message 
        : 'Fehler beim Speichern des Benutzers';
      showSnackbar(errorMessage, 'error');
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Nie';
    
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={onBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" component="h1">
          Benutzerverwaltung
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Box mb={3} display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateUser}
        >
          Neuer Benutzer
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Benutzername</TableCell>
              <TableCell>E-Mail</TableCell>
              <TableCell>Rolle</TableCell>
              <TableCell>Letzte Anmeldung</TableCell>
              <TableCell>Erstellt am</TableCell>
              <TableCell>Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Keine Benutzer gefunden
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.role === 'admin' ? 'Administrator' : 'Benutzer'}
                  </TableCell>
                  <TableCell>{formatDate(user.lastLogin)}</TableCell>
                  <TableCell>{formatDate(user.createdAt)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEditUser(user)}
                      sx={{ mr: 1 }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    
                    {/* Prevent deleting yourself or the last admin */}
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteUser(user)}
                      disabled={
                        user.id === currentUser?.id || 
                        (user.role === 'admin' && 
                         users.filter(u => u.role === 'admin').length <= 1)
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit User Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? 'Benutzer bearbeiten' : 'Neuen Benutzer erstellen'}
        </DialogTitle>
        <DialogContent>
          <TextField
            margin="normal"
            required
            fullWidth
            label="Benutzername"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            disabled={isEditing} // Can't change username for existing users
          />
          
          <TextField
            margin="normal"
            required={!isEditing}
            fullWidth
            label={isEditing ? 'Neues Passwort (leer lassen um beizubehalten)' : 'Passwort'}
            name="password"
            type="password"
            value={formData.password}
            onChange={handleInputChange}
          />
          
          <TextField
            margin="normal"
            required
            fullWidth
            label="E-Mail"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
          />
          
          <TextField
            select
            margin="normal"
            required
            fullWidth
            label="Rolle"
            name="role"
            value={formData.role}
            onChange={handleInputChange}
          >
            <MenuItem value="user">Benutzer</MenuItem>
            <MenuItem value="admin">Administrator</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleSubmit} color="primary">
            {isEditing ? 'Aktualisieren' : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Benutzer löschen</DialogTitle>
        <DialogContent>
          <Typography>
            Sind Sie sicher, dass Sie den Benutzer "{userToDelete?.username}" löschen möchten?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={confirmDeleteUser} color="error">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserAdmin;